import { useState } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { X } from 'lucide-react';
import { img, Rm } from '../../lib/api';
import { CENTROIDS, ContextLabels, FitTo, Legend, METRICS, metricValue, PROV_NAME, ProvinceLayer, TILE_ATTR, TILE_URL, useProvinces } from './geo';

export const STATUS_COL = { Operating: '#10B981', Idle: '#94A3B8', Down: '#EF4444', 'In Service': '#3B82F6', 'Awaiting Delivery': '#22D3EE' };
const HEALTH_COL = { healthy: '#10B981', watchlist: '#F59E0B', distressed: '#EF4444' };

export const assetIcon = (a, sel) => L.divIcon({
  className: '', iconSize: [18, 18], iconAnchor: [9, 9],
  html: `<div data-testid="map-asset-${a.id}" class="${a.status === 'Down' ? 'pulse-ring' : ''}" style="width:${sel ? 18 : 14}px;height:${sel ? 18 : 14}px;border-radius:3px;transform:rotate(45deg);background:${STATUS_COL[a.status] || '#94A3B8'};border:2px solid ${sel ? '#FFCD00' : '#0A0F1D'};box-shadow:0 0 0 1px ${STATUS_COL[a.status]},0 0 12px ${STATUS_COL[a.status]}88"></div>`,
});
export const smeIcon = (s) => L.divIcon({
  className: '', iconSize: [28, 28], iconAnchor: [14, 14],
  html: `<div class="${s.health === 'distressed' ? 'pulse-ring' : ''}" style="width:28px;height:28px;border-radius:50%;border:2px dashed ${HEALTH_COL[s.health]};background:rgba(10,15,29,.35)"></div>`,
});
const clusterIcon = (r, metric) => {
  const size = 30 + Math.min(22, r.assets * 3.5);
  return L.divIcon({
    className: '', iconSize: [140, size + 18], iconAnchor: [70, size / 2 + 16],
    html: `<div class="map-cluster" data-testid="map-cluster-${r.code}"><div class="map-prov-label">${PROV_NAME[r.code]}<span>${METRICS[metric].fmt(metricValue(r, metric))}</span></div>${r.assets ? `<div class="bubble${r.watch ? ' watch' : ''}" style="width:${size}px;height:${size}px"><b>${r.assets}</b><small>assets</small></div>` : ''}</div>`,
  });
};

function ZoomWatch({ onZoom }) {
  useMapEvents({ zoomend: (e) => onZoom(e.target.getZoom()) });
  return null;
}

const Toggle = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`rounded px-2 py-1 text-[11px] transition-colors ${on ? 'bg-[#FFCD00] font-semibold text-[#0A0F1D]' : 'text-slate-300 hover:bg-[#1A2640]'}`}>{children}</button>
);

function ProvinceCard({ r, onClear }) {
  return (
    <div className="absolute bottom-6 left-3 z-[500] w-60 rounded-md border border-[#FFCD00]/40 bg-[#0A0F1D]/92 p-3 backdrop-blur fade-up" data-testid="map-province-card">
      <div className="flex items-center justify-between"><span className="font-display text-base font-semibold text-white">{r.name}</span>
        <button onClick={onClear} className="text-slate-400 hover:text-white" data-testid="map-province-clear"><X className="h-4 w-4" /></button></div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div><div className="eyebrow">Exposure</div><div className="font-mono text-[#FFCD00]">{Rm(r.exposure)}</div></div>
        <div><div className="eyebrow">Assets</div><div className="font-mono text-white">{r.assets}</div></div>
        <div><div className="eyebrow">SMEs</div><div className="font-mono text-white">{r.smes}</div></div>
        <div><div className="eyebrow">On watch</div><div className={`font-mono ${r.watch ? 'text-orange-300' : 'text-emerald-300'}`}>{r.watch}</div></div>
      </div>
      <div className="mt-2 flex gap-3 text-xs"><Link to={`/equipment?province=${r.code}`} className="text-[#FFCD00]" data-testid="map-province-assets-link">Assets →</Link><Link to="/smes" className="text-[#22D3EE]">SMEs →</Link></div>
    </div>
  );
}

export default function PortfolioMap({ assets, smes, regions, province, onProvince, height = 460 }) {
  const geo = useProvinces();
  const [zoom, setZoom] = useState(5.4);
  const [sel, setSel] = useState(null);
  const [metric, setMetric] = useState('exposure');
  const [layers, setLayers] = useState({ assets: true, smes: true, cities: true });
  const stats = Object.fromEntries(regions.map((r) => [r.code, r]));
  const detailed = !!province || zoom >= 7;
  const visA = assets.filter((a) => !province || a.province === province);
  const visS = smes.filter((s) => !province || s.province === province);
  const selRegion = regions.find((r) => r.code === province);
  return (
    <div className="relative overflow-hidden rounded-md border border-[#1E2A45]" style={{ height }} data-testid="portfolio-map">
      <MapContainer center={[-28.9, 25.6]} zoom={5.4} zoomSnap={0.2} minZoom={4.6} maxZoom={14} maxBounds={[[-38, 10], [-18, 38]]} scrollWheelZoom={false} style={{ height: '100%', width: '100%', background: '#0A0F1D' }}>
        <TileLayer url={TILE_URL} attribution={TILE_ATTR} maxZoom={16} />
        {geo && <ProvinceLayer geo={geo} stats={stats} metric={metric} selected={province} onSelect={onProvince} />}
        <FitTo geo={geo} code={province} />
        <ZoomWatch onZoom={setZoom} />
        {layers.cities && <ContextLabels zoom={zoom} />}
        {!(zoom >= 7 && !province) && regions.filter((r) => r.code !== province).map((r) => (
          <Marker key={r.code} position={CENTROIDS[r.code]} icon={clusterIcon(r, metric)} eventHandlers={{ click: () => onProvince(r.code) }} />
        ))}
        {detailed && layers.smes && visS.map((s) => (
          <Marker key={s.id} position={[s.lat, s.lng]} icon={smeIcon(s)}>
            <Popup>
              <div className="w-52 text-xs">
                <div className="font-semibold text-white">{s.name}</div><div className="text-slate-400">{s.sector}</div>
                <div className="mt-1 capitalize" style={{ color: HEALTH_COL[s.health] }}>{s.health}</div>
                <Link to={`/smes/${s.id}`} className="mt-2 inline-block text-[#FFCD00]" data-testid={`map-sme-link-${s.id}`}>Open SME profile →</Link>
              </div>
            </Popup>
          </Marker>
        ))}
        {detailed && layers.assets && visA.map((a) => (
          <Marker key={a.id} position={[a.lat, a.lng]} icon={assetIcon(a, sel === a.id)} eventHandlers={{ click: () => setSel(a.id) }}>
            <Popup>
              <div className="w-56 text-xs" data-testid={`map-popup-${a.id}`}>
                <img src={img(a.image)} alt={`${a.category} — illustrative photograph`} className="mb-2 h-24 w-full rounded object-cover" />
                <div className="font-mono text-[10px] text-[#00A9CE]">{a.id} · {a.category}</div>
                <div className="font-semibold text-white">CAT {a.model}</div>
                <div className="text-slate-400">{a.sme_name} · {a.site}</div>
                <div className="mt-1 flex justify-between"><span style={{ color: STATUS_COL[a.status] }}>{a.status}</span><span>{Rm(a.outstanding)}</span></div>
                <div className="mt-2 flex gap-3">
                  <Link to={`/equipment/${a.id}`} className="text-[#FFCD00]" data-testid={`map-asset-link-${a.id}`}>Open asset →</Link>
                  <Link to={`/smes/${a.sme_id}`} className="text-[#22D3EE]">SME →</Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="absolute left-14 top-3 z-[500] flex flex-wrap gap-2">
        <div className="flex gap-0.5 rounded-md border border-[#2B3B60] bg-[#0A0F1D]/90 p-0.5 backdrop-blur" data-testid="map-metric-toggle">
          {Object.entries(METRICS).map(([k, m]) => <Toggle key={k} on={metric === k} onClick={() => setMetric(k)} testid={`map-metric-${k}`}>{m.label}</Toggle>)}
        </div>
        <div className="flex gap-0.5 rounded-md border border-[#2B3B60] bg-[#0A0F1D]/90 p-0.5 backdrop-blur" data-testid="map-layer-toggle">
          {[['assets', 'Assets'], ['smes', 'SME sites'], ['cities', 'Labels']].map(([k, l]) => <Toggle key={k} on={layers[k]} onClick={() => setLayers({ ...layers, [k]: !layers[k] })} testid={`map-layer-${k}`}>{l}</Toggle>)}
        </div>
      </div>
      <div className="pointer-events-none absolute right-3 top-3 z-[500] space-y-2">
        <Legend stats={stats} metric={metric} />
        {detailed && <div className="rounded border border-[#2B3B60] bg-[#0A0F1D]/85 px-2.5 py-2 text-[10px] text-slate-300 backdrop-blur">
          {Object.entries(STATUS_COL).map(([k, v]) => <div key={k} className="flex items-center gap-1.5"><span className="h-2 w-2 rotate-45" style={{ background: v }} />{k}</div>)}
          <div className="mt-1 flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-dashed border-amber-400" />SME watchlist ring</div></div>}
      </div>
      {selRegion && <ProvinceCard r={selRegion} onClear={() => onProvince(null)} />}
      <div className="pointer-events-none absolute bottom-1 left-3 z-[500] font-mono text-[9px] uppercase tracking-wider text-slate-500">Synthetic locations · not verified physical tracking</div>
    </div>
  );
}
